import { SpinTransportError } from "@slot/client-engine";
import type { PresentationCompleteRequest, PresentationCompleteResponse, SpinRequest, SpinResult } from "@slot/client-engine";
import type { GameDefinition } from "@slot/game-shell";
import { LINE_OPTIONS } from "../rules/lines";
import { EMPTY_POTS } from "../rules/symbols";
import { playRound, type OfflineServerState } from "./holdGame";
import { BONUS_BUYS, isBonusBuyId, type BonusBuyId } from "./offlineMath";
import { buildRound } from "./RoundBuilder";
import type { ScriptedFixture } from "./fixtureScripts";
import { scriptedRound } from "./scriptedRound";
import type { Rng } from "./rng";

type OfflineRoundProvider = ReturnType<GameDefinition["fixtures"]["createRoundProvider"]>;
type FixtureOption = ReturnType<OfflineRoundProvider["listFixtures"]>[number];
type RoundFaults = ReturnType<OfflineRoundProvider["getFaults"]>;
type SpinRejectionCode = NonNullable<Parameters<OfflineRoundProvider["rejectNextSpin"]>[0]>;

/**
 * coin-hold's offline server: the backend this title does not have yet, played in the page (every build without a
 * backend URL, staging included). It keeps a wallet and the pots between rounds, plays each spin request with the
 * game's rules (`holdGame.ts`) at the request's bet over its lines (`requestCustomPayload.betLines`, decision 56: the
 * bet is their total) — or the bought bonus — and answers in the wire's shape
 * (`RoundBuilder`). The dev tools drive it like the shell's `FixtureRoundProvider` (decision 37): a playlist of the
 * scripted rounds replaces the random spins until cleared (a bought bonus is always played at random), and one-shot
 * faults hit the next requests.
 */
export class CoinHoldOfflineServer implements OfflineRoundProvider {
  private readonly fixtures: readonly ScriptedFixture[];
  private readonly fixturesById: ReadonlyMap<string, ScriptedFixture>;
  private readonly rng: Rng;
  private readonly currencyCode: string;
  private readonly state: OfflineServerState = { pots: EMPTY_POTS, lastLineBetMinor: null };
  private balance: bigint;
  private readonly winsByRound = new Map<string, bigint>();
  private readonly settled = new Set<string>();
  private readonly sessionStamp: string;
  private roundCounter = 0;
  private playlist: readonly string[] = [];
  private cursor = 0;
  private nextSpinFault: RoundFaults["nextSpin"] = null;
  private settlementFailuresLeft = 0;
  private latencyMs: number;

  public constructor(input: {
    fixtures: readonly ScriptedFixture[];
    rng: Rng;
    /** The wallet at boot: the offline settings' balance, so the bar and the server agree. */
    startingBalanceMinor: string;
    currencyCode: string;
    simulatedLatencyMs: number;
    /** Distinguishes this page's round ids from another session's. */
    sessionStamp: string;
  }) {
    this.fixtures = input.fixtures;
    this.fixturesById = new Map(input.fixtures.map((fixture) => [fixture.id, fixture]));
    if (this.fixturesById.size !== input.fixtures.length) throw new Error("CoinHoldOfflineServer: two scripted rounds share an id.");
    this.rng = input.rng;
    this.balance = BigInt(input.startingBalanceMinor);
    this.currencyCode = input.currencyCode;
    this.latencyMs = input.simulatedLatencyMs;
    this.sessionStamp = input.sessionStamp;
  }

  // ── FixturePlaylistPort ─────────────────────────────────────────────────────────────────────────────────────────

  public listFixtures(): readonly FixtureOption[] {
    return this.fixtures.map(({ id, label, group }) => ({ id, label, group }));
  }

  public getPlaylist(): readonly string[] {
    return this.playlist;
  }

  public getPlaylistCursor(): number {
    return this.cursor;
  }

  public setPlaylist(ids: readonly string[]): void {
    const unknown = ids.filter((id) => !this.fixturesById.has(id));
    if (unknown.length > 0) throw new Error(`Unknown fixture ${unknown.map((id) => `"${id}"`).join(", ")}. Known: ${[...this.fixturesById.keys()].join(", ")}`);
    this.playlist = [...ids];
    this.cursor = 0;
  }

  // ── RoundFaultPort ──────────────────────────────────────────────────────────────────────────────────────────────

  public rejectNextSpin(code: SpinRejectionCode = "insufficient_funds"): void {
    this.nextSpinFault = code;
  }

  public failNextSpin(): void {
    this.nextSpinFault = "lost_reply";
  }

  public failNextSettlement(count = 1): void {
    this.settlementFailuresLeft = count;
  }

  public setSimulatedLatency(ms: number): void {
    this.latencyMs = Math.max(0, ms);
  }

  public getFaults(): RoundFaults {
    return { nextSpin: this.nextSpinFault, settlementFailures: this.settlementFailuresLeft, latencyMs: this.latencyMs };
  }

  // ── RoundProvider ───────────────────────────────────────────────────────────────────────────────────────────────

  public async requestRoundSnapshot(request: SpinRequest): Promise<SpinResult> {
    await this.latency();
    const fault = this.nextSpinFault;
    this.nextSpinFault = null;
    if (fault === "player_spin_in_flight") {
      return { schemaVersion: "3", serverTimeIsoString: new Date().toISOString(), responseMetadata: { statusCode: 409, statusMessage: "player_spin_in_flight", statusData: { detail: "another spin for this player is already in flight" } } };
    }
    const buyId = request.requestedFeatureIdentifier ?? null;
    if (buyId !== null && !isBonusBuyId(buyId)) {
      return this.rejection(400, "unknown_feature");
    }
    // The lines the bet covers: one of the counts offered, and the bet whole lines of a line's bet.
    const lines = request.requestCustomPayload?.["betLines"];
    if (typeof lines !== "number" || !LINE_OPTIONS.includes(lines)) return this.rejection(400, "invalid_lines");
    const bet = BigInt(request.betAmount);
    if (bet <= 0n || bet % BigInt(lines) !== 0n) return this.rejection(400, "invalid_bet");
    const cost = buyId ? bet * BigInt(BONUS_BUYS[buyId].price) : bet;
    if (fault === "insufficient_funds" || cost > this.balance) return this.rejection(402, "insufficient_funds");

    this.balance -= cost;
    const response = this.play(request.betAmount, lines, buyId);
    if (fault === "lost_reply") {
      throw new SpinTransportError("network", "CoinHoldOfflineServer: simulated lost reply — the round was played, the reply never arrived");
    }
    return response;
  }

  public async acknowledgePresentationComplete(request: PresentationCompleteRequest): Promise<PresentationCompleteResponse> {
    await this.latency();
    if (this.settlementFailuresLeft > 0) {
      this.settlementFailuresLeft -= 1;
      throw new SpinTransportError("network", "CoinHoldOfflineServer: simulated settlement failure — the acknowledgement was lost on the way");
    }
    // A round is credited once, however often it is acknowledged (the backend's rule, decision 17).
    if (!this.settled.has(request.roundIdentifier)) {
      this.settled.add(request.roundIdentifier);
      this.balance += this.winsByRound.get(request.roundIdentifier) ?? 0n;
    }
    return {
      roundIdentifier: request.roundIdentifier,
      accountBalanceSnapshot: { currencyCode: this.currencyCode, balanceAmount: this.balance.toString() },
      serverTimeIsoString: new Date().toISOString(),
    };
  }

  /** The round for a request the wallet covered: the playlist's next scripted round, else a random one (a buy always). */
  private play(betMinor: string, lines: number, buyId: BonusBuyId | null): SpinResult {
    const scripted = buyId === null && this.playlist.length > 0 ? this.fixturesById.get(this.playlist[this.cursor]!)! : null;
    if (scripted) this.cursor = (this.cursor + 1) % this.playlist.length;
    const outcome = scripted ? scriptedRound(betMinor, lines, scripted.script, this.state) : playRound(this.rng, this.state, betMinor, lines, buyId);
    this.roundCounter += 1;
    const roundIdentifier = `offline-${this.sessionStamp}-${this.roundCounter}${scripted ? `-${scripted.id}` : ""}`;
    this.winsByRound.set(roundIdentifier, BigInt(outcome.winMinor));
    return buildRound({ roundIdentifier, outcome, currencyCode: this.currencyCode, balanceMinor: this.balance.toString() });
  }

  private rejection(statusCode: number, statusMessage: string): SpinResult {
    return {
      schemaVersion: "3",
      serverTimeIsoString: new Date().toISOString(),
      accountBalanceSnapshot: { currencyCode: this.currencyCode, balanceAmount: this.balance.toString() },
      responseMetadata: { statusCode, statusMessage },
    };
  }

  private async latency(): Promise<void> {
    if (this.latencyMs > 0) await new Promise((resolve) => setTimeout(resolve, this.latencyMs));
  }
}
